import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLandingSettings, isUsableUrl } from "@/context/LandingSettingsContext";

const NAV = [
  { label: "Collection", href: "/#collection" },
  { label: "About",      href: "/#about" },
  { label: "Utility",    href: "/#utilities" },
  { label: "Games",      href: "/#games" },
  { label: "Lore",       href: "/#lore" },
  { label: "The Oracle", href: "/theoracle" },
];

const SOCIAL_ICONS = {
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
      <path d="M18.244 2H21l-6.535 7.47L22 22h-6.828l-4.77-6.24L4.8 22H2l7.02-8.02L2 2h6.914l4.32 5.71L18.244 2Zm-2.394 18h1.83L8.244 4H6.3l9.55 16Z" />
    </svg>
  ),
  discord: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-4 h-4">
        <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3a13.29 13.29 0 0 0-.62 1.26 18.28 18.28 0 0 0-5.876 0A13.35 13.35 0 0 0 9.443 3a19.79 19.79 0 0 0-3.76 1.369C2.146 9.7 1.32 14.9 1.66 20.02a19.86 19.86 0 0 0 6.032 3.06c.487-.66.919-1.363 1.29-2.1a12.99 12.99 0 0 1-2.03-.98c.17-.126.335-.257.494-.39a14.15 14.15 0 0 0 12.11 0c.16.133.324.264.494.39-.647.383-1.327.716-2.03.98.37.737.803 1.44 1.29 2.1a19.83 19.83 0 0 0 6.032-3.06c.42-5.87-.72-11.02-3.024-15.65ZM9.03 16.66c-1.183 0-2.157-1.09-2.157-2.43 0-1.34.96-2.44 2.157-2.44 1.197 0 2.17 1.1 2.157 2.44 0 1.34-.96 2.43-2.157 2.43Zm5.94 0c-1.184 0-2.158-1.09-2.158-2.43 0-1.34.96-2.44 2.158-2.44 1.196 0 2.17 1.1 2.157 2.44 0 1.34-.96 2.43-2.157 2.43Z" />
      </svg>
  ),
};

export function ProphetsHeader({ onLogoClick }) {
  const { settings } = useLandingSettings();
  const socials = [
    { key: "x",       label: "X",       href: settings.twitter_url },
    { key: "discord", label: "Discord", href: settings.discord_url },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  // Brand link: from any Prophets page, clicking the PROPHETS logo must
  // navigate back to `/` and land at scroll position 0 — not preserve the
  // previous scroll position. Uses React Router for a real route change
  // when the user is not already on `/`; otherwise just scrolls to top.
  const scrollTop = (e) => {
    e.preventDefault();
    closeMenu();
    if (location.pathname !== "/") {
      navigate("/");
      // After the route swap paints, force scroll to the top of the landing
      // page (React Router does not scroll on route change by default).
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        // Second frame guards against layout shifts (font/image swap-in).
        requestAnimationFrame(() => window.scrollTo(0, 0));
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    onLogoClick?.();
  };

  return (
    <header
      data-testid="prophets-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-md border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] flex items-center justify-between gap-6 px-5 md:px-10 h-16 md:h-20">
        {/* Left: symbol + wordmark */}
        <a
          href="/"
          onClick={scrollTop}
          aria-label="Prophets — back to top"
          data-testid="prophets-brand"
          className="flex items-center gap-2.5 group shrink-0"
        >
          <img
            src="/img/prophets_logo_mark.webp"
            alt="Prophets"
            width="28"
            height="28"
            className="h-6 md:h-7 w-auto opacity-90 group-hover:opacity-100 transition-opacity"
            style={{
              objectFit: "contain",
              filter: "drop-shadow(0 0 6px rgba(200,210,230,0.15))",
            }}
          />
          <span
            className="p-nav text-[11px] md:text-xs tracking-[0.32em] text-white/85 group-hover:text-white transition-colors"
          >
            PROPHETS
          </span>
        </a>

        {/* Center nav (desktop) */}
        <nav
          data-testid="prophets-nav"
          className="hidden lg:flex items-center gap-10 xl:gap-14"
          aria-label="Primary navigation"
        >
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.href.slice(1)}`}
              className="prophets-nav-link p-nav text-[11px] tracking-[0.28em] uppercase"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right: socials + Mint */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <div className="hidden sm:flex items-center gap-1">
            {socials.map((s) => {
              const usable = isUsableUrl(s.href);
              const className = "w-9 h-9 grid place-items-center rounded-full text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors";
              return usable ? (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.key}`}
                  className={className}
                >
                  {SOCIAL_ICONS[s.key]}
                </a>
              ) : (
                <button
                  key={s.key}
                  type="button"
                  aria-label={s.label}
                  aria-disabled="true"
                  data-testid={`social-${s.key}`}
                  onClick={(e) => e.preventDefault()}
                  className={className + " bg-transparent border-0 p-0"}
                >
                  {SOCIAL_ICONS[s.key]}
                </button>
              );
            })}
          </div>
          {/* Repurposed from the (finished) MINT button — same size,
              shape and typography, now labelled HUB and points to the
              Prophets Hub route. Elegant blue → purple Solana gradient
              via `.prophets-hub-btn`. */}
          <a
            href="/hub"
            data-testid="header-hub"
            className="prophets-hub-btn p-nav hidden md:inline-flex items-center px-5 py-2 rounded-sm text-[10px] tracking-[0.32em] uppercase text-white"
          >
            Hub
          </a>
          <button
            data-testid="prophets-menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full text-white/85 hover:bg-white/[0.06] transition-colors"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="prophets-mobile-nav"
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
          menuOpen ? "max-h-[420px]" : "max-h-0"
        } bg-black/85 backdrop-blur-md border-t border-white/[0.06]`}
      >
        <nav className="flex flex-col px-6 py-6 gap-4" aria-label="Mobile navigation">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={closeMenu}
              className="p-nav text-white/80 hover:text-white text-sm tracking-[0.3em] uppercase py-1.5"
            >
              {item.label}
            </a>
          ))}
          <div className="flex items-center gap-4 pt-4 border-t border-white/[0.06]">
            {socials.map((s) => {
              const usable = isUsableUrl(s.href);
              const cls = "w-9 h-9 grid place-items-center rounded-full text-white/75 hover:text-white hover:bg-white/[0.06]";
              return usable ? (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  onClick={closeMenu}
                  className={cls}
                >
                  {SOCIAL_ICONS[s.key]}
                </a>
              ) : (
                <button
                  key={s.key}
                  type="button"
                  aria-label={s.label}
                  aria-disabled="true"
                  onClick={(e) => { e.preventDefault(); closeMenu(); }}
                  className={cls + " bg-transparent border-0 p-0"}
                >
                  {SOCIAL_ICONS[s.key]}
                </button>
              );
            })}
            {/* Mobile drawer version of the HUB button (same gradient). */}
            <a
              href="/hub"
              onClick={closeMenu}
              data-testid="header-hub-mobile"
              className="prophets-hub-btn ml-auto p-nav inline-flex items-center px-5 py-2 rounded-sm text-[10px] tracking-[0.32em] uppercase text-white"
            >
              Hub
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
